import type { Editor as CoreEditor } from "@tiptap/core";
import type { MenuItem } from "@v1/components/designSystem/menu/Menu";
import type { DeprecatedToggleOption } from "@v1/components/designSystem/toggle/ToggleGroup";
import type { UseApplyComponentActionType } from "@v1/hooks/useApplyComponentAction";
import type { RichTextEditorTag } from "@v1/types/rich-text-editor";
import type { SolidOrGradient } from "replo-runtime/shared/types";
import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import type { SavedStyleTextAttributes } from "schemas/generated/savedStyles";
import type { TextControlType } from "schemas/modifiers";
import type { GradientStop } from "schemas/styleAttribute";
import type { TextShadow } from "schemas/textShadow";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { selectActiveCanvas } from "@v1/components/canvas/canvas-reducer";
import DynamicDataButton from "@v1/components/common/designSystem/DynamicDataButton";
import SelectionIndicator from "@v1/components/common/designSystem/SelectionIndicator";
import FormFieldXButton from "@v1/components/common/FormFieldXButton";
import TextShopStyleModifier from "@v1/components/designLibrary/DesignLibraryTextValueIndicator";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { Menu } from "@v1/components/designSystem/menu/Menu";
import Popover from "@v1/components/designSystem/popover/Popover";
import SelectableButtons from "@v1/components/designSystem/selectable-buttons/SelectableButtons";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import {
  BADGE_TRIGGER_OFFSET,
  TIPTAP_EDITOR_SCROLLABLE_DIV_ID,
} from "@v1/components/editor/constants";
import { DynamicDataSelector } from "@v1/components/editor/page/DynamicDataSelector";
import DocumentationInfoIcon from "@v1/components/editor/page/element-editor/components/DocumentationInfoIcon";
import ControlGroup from "@v1/components/editor/page/element-editor/components/extras/ControlGroup";
import DynamicDataValueIndicator from "@v1/components/editor/page/element-editor/components/extras/DynamicDataValueIndicator";
import ModifierGroup from "@v1/components/editor/page/element-editor/components/extras/ModifierGroup";
import ModifierLabel from "@v1/components/editor/page/element-editor/components/extras/ModifierLabel";
import { LengthInputSelector } from "@v1/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import {
  getCurrentTag,
  getUpdatedLineHeightForFontSize,
} from "@v1/components/editor/page/element-editor/components/modifiers/utils";
import ResizablePane from "@v1/components/ResizablePane";
import { useRichTextComponent } from "@v1/components/RichTextComponentContext";
import TipTapToolbar from "@v1/components/TipTapRichTextToolbar";
import Tiptap, { useTipTapEditor } from "@v1/components/TiptapTextEditor";
import useGetDeletedSavedStyleValueIfNeeded from "@v1/hooks/designLibrary/useGetDeletedSavedStyleValueIfNeeded";
import useShopStyles from "@v1/hooks/designLibrary/useGetDesignLibrarySavedStyles";
import useResetDesignLibraryTextValue from "@v1/hooks/designLibrary/useResetDesignLibraryTextValue";
import { useGetModifierControls } from "@v1/hooks/rightBar/useGetModifierControls";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import { useEnableNonDynamicTextEditing } from "@v1/hooks/useEnableNonDynamicTextEditing";
import { useGlobalEditorActions } from "@v1/hooks/useGlobalEditorActions";
import {
  selectAncestorTextColor,
  selectColor,
  selectColorGradientStops,
  selectColorGradientTilt,
  selectDraftComponent,
  selectDraftComponentComputedStyleValue,
  selectDraftComponentId,
  selectDraftComponentSellingPlans,
  selectDraftComponentsInnerTextsAndIds,
  selectDraftComponentText,
  selectDraftComponentTextDynamicDataInfo,
  selectDraftComponentTextTagWithMixedValues,
  selectEvaluatedDraftComponentText,
  selectFontFamily,
  selectFontSize,
  selectFontStyle,
  selectFontWeight,
  selectGetAttribute,
  selectLineHeight,
  selectSavedStyle,
  selectTextAlign,
  selectTextDecoration,
  selectTextOutline,
  selectTextShadow,
  selectTextShadows,
  selectTextTransform,
} from "@v1/reducers/core-reducer";
import {
  selectOpenPopoverId,
  setIsRichTextEditorFocused,
  setOpenPopoverId,
} from "@v1/reducers/ui-reducer";
import {
  convertCSSStylesToReploStyles,
  getValidFilteredStyleProps,
} from "@v1/reducers/utils/component-actions";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import { getEditorComponentNode } from "@v1/utils/component";
import { getPathFromVariable } from "@v1/utils/dynamic-data";
import { useInCanvasPreview } from "@v1/utils/preview";
import { isAllTextColored } from "@v1/utils/rte";
import { stripHtmlTags } from "@v1/utils/string";
import { styleAttributeToEditorData } from "@v1/utils/styleAttribute";
import { getTextOutlineString } from "@v1/utils/textOutline";
import {
  formatTitle,
  getTextShadowString,
  parseTextShadows,
} from "@v1/utils/textShadow";
import { useReploHotkeys } from "@v2/hooks/useHotkeys";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useAreModalsOpen } from "@v2/store/modalStore";
import { getHexColor } from "@v2/utils/colors";
import { docs } from "@v2/utils/docs";
import { naiveRemoveHtmlTags } from "@v2/utils/dom";
import debounce from "lodash-es/debounce";
import differenceBy from "lodash-es/differenceBy";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Baseline,
  Hash,
  Italic,
  MoreHorizontal,
  Strikethrough,
  Underline,
} from "lucide-react";
import { EditorView } from "prosemirror-view";
import { isDynamicDataValue } from "replo-runtime";
import { getSavedStyleValue } from "replo-runtime/shared/savedStyles";
import {
  isDynamicDesignLibraryValue,
  resolveTextWithSavedStyles,
} from "replo-runtime/shared/utils/designLibrary";
import { getDynamicDataReferenceType } from "replo-runtime/shared/utils/dynamic-data";
import { CSS_LENGTH_TYPES } from "replo-runtime/shared/utils/units";
import {
  isMixedStyleValue,
  REPLO_MIXED_STYLE_VALUE,
} from "replo-runtime/store/utils/mixed-values";
import { filterNulls } from "replo-utils/lib/array";
import { coerceNumberToString } from "replo-utils/lib/misc";
import { v4 as uuidv4 } from "uuid";

import { DynamicDataFormatter } from "../../../DynamicDataFormatter";
import FontSizeSelector from "../selectors/text-style/FontSizeSelector";
import FontWeightSelector from "../selectors/text-style/FontWeightSelector";
import LetterSpacingSelector from "../selectors/text-style/LetterSpacingSelector";
import LineHeightSelector from "../selectors/text-style/LineHeightSelector";
import TagSelector from "../selectors/text-style/TagSelector";
import DynamicColorModifier, {
  DynamicColorSelector,
} from "./DynamicColorModifier";
import FontFamilyControl from "./FontFamilyControl";

// NOTE (Reinaldo 2022-06-03): Hacky solution to fix editor breaking when selecting a text node
// https://github.com/ueberdosis/tiptap/issues/1451#issuecomment-953348865
EditorView.prototype.updateState = function updateState(state) {
  // @ts-ignore
  if (!this.docView) {
    return;
  } // This prevents the matchesNode error on hot reloads
  // @ts-ignore
  this.updateStateInner(state, this.state.plugins != state.plugins);
};

const DEFAULT_TEXT_SHADOW = {
  id: uuidv4(),
  offsetX: "0px",
  offsetY: "4px",
  blur: "1px",
  color: "#00000040",
};

const TEXT_TRANSFORM_OPTIONS: DeprecatedToggleOption[] = [
  {
    value: "capitalize",
    label: "Ag",
    tooltipContent: "Capitalize",
  },
  {
    value: "uppercase",
    label: "AG",
    tooltipContent: "Uppercase",
  },
  {
    value: "lowercase",
    label: "ag",
    tooltipContent: "Lowercase",
  },
];

const MIN_EDITOR_HEIGHT = 40;
const CUTOFF_EDITOR_EXPANSION = 200;

const TextStyleModifier: React.FC<{
  includeTextEditor: boolean;
}> = ({ includeTextEditor }) => {
  useTextStyleHotkeys();

  const groupRef = React.useRef<HTMLDivElement>(null);
  const [controls, addControl] = useGetModifierControls<"text">(
    "text",
    groupRef,
  );
  const handleAddTextShadow = useAddDefaultShadow();
  const fontSize = useEditorSelector(selectFontSize);
  const isDesignLibraryValue = isDynamicDesignLibraryValue(
    String(fontSize) ?? "",
  );

  const menuItems: MenuItem[] = [
    {
      id: "textDecoration",
      title: "Format",
      type: "leaf",
      variant: "default",
      size: "sm",
      onSelect: () => addControl("textDecoration"),
      disabled: controls.has("textDecoration") || isDesignLibraryValue,
    },
    {
      id: "textTransform",
      title: "Casing",
      type: "leaf",
      variant: "default",
      size: "sm",
      onSelect: () => addControl("textTransform"),
      disabled: controls.has("textTransform") || isDesignLibraryValue,
    },
    {
      id: "textOutline",
      title: "Outline",
      type: "leaf",
      variant: "default",
      size: "sm",
      onSelect: () => addControl("textOutline"),
      disabled: controls.has("textOutline") || isDesignLibraryValue,
    },
    {
      id: "textShadow",
      title: "Shadow",
      type: "leaf",
      variant: "default",
      size: "sm",
      onSelect: () => {
        addControl("textShadow");
        handleAddTextShadow();
      },
      disabled: isDesignLibraryValue,
    },
  ];

  const mapControlsToComponent: Array<{
    property: TextControlType;
    component: React.ReactNode;
  }> = [
    { property: "textAlign", component: <TextAlignmentControl /> },
    {
      property: "fontFamily",
      component: (
        <div id="font-family-selectable">
          <FontFamilyControl />
        </div>
      ),
    },
    {
      property: "fontWeight",
      component: <FontWeightControl />,
    },
    {
      property: "fontSize",
      component: <FontSizeControl />,
    },
    {
      property: "lineHeight",
      component: <LineHeightControl />,
    },
    { property: "color", component: <ForegroundColorControl /> },
    {
      property: "letterSpacing",
      component: <LetterSpacingControl />,
    },
    {
      property: "textDecoration",
      component: <TextFormatControl />,
    },
    {
      property: "htmlTag",
      component: <TagControl />,
    },
    {
      property: "textTransform",
      component: <TextTransformControl />,
    },
    {
      property: "textOutline",
      component: <TextOutlineControl />,
    },
    {
      property: "textShadow",
      component: <TextShadowControl />,
    },
  ];

  return (
    <ModifierGroup
      ref={groupRef}
      title="Text"
      menuItems={menuItems}
      tooltipText="Add Additional Text Format"
      // Note (Noah, 2024-10-25): Disable focusing the menu trigger on close, since this will
      // dismiss the popover that opens when we add a text shadow
      disableMenuTriggerFocusOnClose
      endEnhancer={<HeaderMenu />}
      titleEnhancer={<DocumentationInfoIcon href={docs.modifiers.text} />}
    >
      <div id="text-style-modifier" className="flex flex-col gap-2">
        {includeTextEditor && <TextControl />}
        <TextSavedStyleGroup />
        {mapControlsToComponent.map(({ property, component }) => {
          if (controls.has(property) && component) {
            return <React.Fragment key={property}>{component}</React.Fragment>;
          }
          return null;
        })}
      </div>
    </ModifierGroup>
  );
};

const HeaderMenu: React.FC = () => {
  const applyComponentAction = useApplyComponentAction();
  const colorValue = useEditorSelector(selectColor);
  const textValue = useEditorSelector(selectDraftComponentText);
  return (
    <Menu
      items={[
        {
          type: "leaf",
          id: "pasteFromFigma",
          title: "Paste Style From Figma",
          variant: "default",
          size: "sm",
          onSelect: () => {
            void navigator.clipboard.readText().then((text) => {
              const styleObject = convertCSSStylesToReploStyles(text, true);
              const { style: textValidStyles } = getValidFilteredStyleProps(
                { style: styleObject },
                "text",
                {
                  colorValue: !isMixedStyleValue(colorValue)
                    ? colorValue ?? null
                    : null,
                  textValue: textValue ?? "",
                },
              );
              applyComponentAction({
                type: "setStyles",
                value: textValidStyles ?? {},
              });
            });
          },
        },
      ]}
      customWidth={200}
      triggerAsChild
      trigger={
        <DeprecatedIconButton
          variant="tertiary"
          size="sm"
          icon={<MoreHorizontal className="h-4 w-4" />}
        />
      }
    />
  );
};

const TextControl = () => {
  const text = useEditorSelector(selectDraftComponentText);

  if (isMixedStyleValue(text)) {
    return null;
  }

  return typeof text === "string" && isDynamicDataValue(text) ? (
    <>
      <ControlGroup label="Text">
        <DynamicTextControl />
      </ControlGroup>
      <DynamicTextFormatControl />
    </>
  ) : (
    <RichTextControl />
  );
};

const DynamicTextControl = () => {
  const text = useEditorSelector(selectDraftComponentText);
  const evaluatedText = useEditorSelector(selectEvaluatedDraftComponentText);
  const draftComponentId = useEditorSelector(selectDraftComponentId);
  const applyComponentAction = useApplyComponentAction();
  const handleChange = (newValue: string) => {
    applyComponentAction({
      type: "setProps",
      value: {
        text: newValue,
      },
    });
  };

  const handleRemove = () => {
    applyComponentAction({
      type: "applyCompositeAction",
      value: [
        {
          type: "setProps",
          value: { text: evaluatedText ?? "Add new text here" },
          analyticsExtras: {
            actionType: "edit",
            createdBy: "replo",
          },
        },
      ],
    });
  };

  return (
    <div className="col-span-2 w-full">
      <DynamicDataSelector
        side="left"
        sideOffset={86}
        targetType="text"
        initialPath={getPathFromVariable(text ?? "")}
        onChange={handleChange}
        onRemove={handleRemove}
        triggerClassName="w-full"
        trigger={
          <DynamicDataValueIndicator
            type="text"
            labelClassname="text-left"
            templateValue={text}
            componentId={draftComponentId ?? undefined}
            onRemove={handleRemove}
          />
        }
      ></DynamicDataSelector>
    </div>
  );
};
const DynamicTextFormatControl = () => {
  const applyComponentAction = useApplyComponentAction();
  const dynamicDataInfoList = useEditorSelector(
    selectDraftComponentTextDynamicDataInfo,
  );
  const dynamicDataInfo = dynamicDataInfoList?.[0];
  const formatters = dynamicDataInfo?.formatters;
  const referenceType =
    dynamicDataInfo?.path && getDynamicDataReferenceType(dynamicDataInfo.path);
  const _evaluatedText = useEditorSelector(selectEvaluatedDraftComponentText);
  const evaluatedText = _evaluatedText && stripHtmlTags(_evaluatedText);
  const sellingPlans = useEditorSelector(selectDraftComponentSellingPlans);
  const handleChange = (newValue: string) => {
    applyComponentAction({
      type: "setProps",
      value: {
        text: newValue,
      },
    });
  };
  if (!referenceType || !evaluatedText) {
    return null;
  }
  return (
    <ControlGroup label="Format">
      <div className="col-span-2 w-full">
        <DynamicDataFormatter
          side="left"
          sideOffset={86}
          initialValue={formatters ?? []}
          referenceType={referenceType}
          onChange={handleChange}
          triggerClassName="w-full"
          sellingPlans={sellingPlans}
          trigger={
            <SelectionIndicator
              title={evaluatedText}
              labelClassname="text-left"
              startEnhancer={<Hash size={16} className="text-placeholder" />}
            />
          }
        />
      </div>
    </ControlGroup>
  );
};

const toolbarId = "pane-rte-toolbar";

const RichTextControl = () => {
  const { designLibrary } = useShopStyles();
  const dispatch = useEditorDispatch();
  const { applyChanges, setTipTapEditor, setTag } = useRichTextComponent();
  const onChangeRichTextEditor = React.useMemo(
    () => debounce((newValue: string) => applyChanges(newValue), 300),
    [applyChanges],
  );

  const onBlurRichTextEditor = React.useCallback(() => {
    dispatch(setIsRichTextEditorFocused(false));
  }, [dispatch]);

  const onFocusRichTextEditor = React.useCallback(() => {
    dispatch(setIsRichTextEditorFocused(true));
  }, [dispatch]);

  const onCreate = (editor: CoreEditor | null) => {
    setTag(getCurrentTag(editor));
  };

  const text = useEditorSelector(selectDraftComponentText);
  const resolvedText = text
    ? resolveTextWithSavedStyles(text, designLibrary ?? null)
    : text;

  const tipTapEditor = useTipTapEditor(
    resolvedText,
    onChangeRichTextEditor,
    onBlurRichTextEditor,
    onFocusRichTextEditor,
    onCreate,
  );

  const isRichTextEditorFocused = useEditorSelector(
    (state) => state.ui.isRichTextEditorFocused,
  );

  const applyComponentAction = useApplyComponentAction();

  const handleSetDynamicData = (newValue: string) => {
    applyComponentAction({
      type: "setProps",
      value: {
        text: newValue,
      },
    });
  };

  const [shouldAutoResize, setShouldAutoResize] = React.useState(true);
  const [resizableHeight, setResizableHeight] =
    React.useState(MIN_EDITOR_HEIGHT);
  const [currentEditorHeight, setCurrentEditorHeight] =
    React.useState(MIN_EDITOR_HEIGHT);

  // Synchronize text changes to the editor that don't originate from the
  // editor.
  const textRef = React.useRef(resolvedText);
  React.useEffect(() => {
    const prevText = textRef.current;
    textRef.current = resolvedText;
    if (!isRichTextEditorFocused && tipTapEditor && resolvedText !== prevText) {
      tipTapEditor.commands.setContent(resolvedText!);
      setShouldAutoResize(true);
    }
  }, [isRichTextEditorFocused, tipTapEditor, resolvedText]);

  React.useEffect(() => {
    // pass this instance of the editor to context so it can be used elsewhere
    if (tipTapEditor) {
      setTipTapEditor(tipTapEditor);
    }
    // Make sure to remove the editor from context when this component is
    // unmounted
    return () => {
      setTipTapEditor(null);
    };
  }, [setTipTapEditor, tipTapEditor]);

  // NOTE (Max 2024-05-08): Listens for when editor's height changes, as we need
  // to update the resizable's component height accordingly. We only allow to
  // automatically increase the height until cutoffEditorExpansion
  React.useEffect(() => {
    const editorDiv = document.getElementById(TIPTAP_EDITOR_SCROLLABLE_DIV_ID);

    if (editorDiv && shouldAutoResize) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { height } = entry.contentRect;
          setCurrentEditorHeight(height);
          if (height <= CUTOFF_EDITOR_EXPANSION) {
            // NOTE (Sebas, 2024-10-22): If the text editor is smaller than the minimum height, we set the
            // current editor height to the minimum height. This is needed to prevent a weird flash on the
            // text editor when the user selects a text component. Also if the user tries to expand the editor
            // the size will be set automatically to the minimum height without moving the mouse, which was weird.
            setResizableHeight(
              height <= MIN_EDITOR_HEIGHT ? MIN_EDITOR_HEIGHT : height + 10,
            );
          } else {
            setResizableHeight(CUTOFF_EDITOR_EXPANSION);
          }
        }
      });

      resizeObserver.observe(editorDiv);
      return () => resizeObserver.disconnect();
    }
  }, [shouldAutoResize]);

  // NOTE (Max 2024-05-08): In the context of the ResizablePane's height being larger than the
  // actual editor's height, we want the user to be able to click on ResizablePane and still
  // trigger the editor. Otherwise, the only place where user could click to start typing
  // would be within the actual editor - not good UX.
  const propagateClick = () => {
    const editorDiv = document.getElementById(
      "in-tiptap-editor",
    ) as HTMLElement | null;
    editorDiv?.focus();
  };

  return (
    <>
      <div
        className="col-span-2"
        data-testid="rich-text-editor"
        // NOTE (Sebas, 2024-06-27): We need to stop propagation of the keydown event
        // to prevent the hotkeys from being triggered.
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="bg-sheet border-0.5 border-component-border focus-within:ring-primary relative flex flex-col gap-2 rounded-sm p-2 focus-within:ring-1 focus-within:ring-inset focus-within:ring-offset-0 focus-within:ring-offset-transparent">
          <TipTapToolbar editor={tipTapEditor} id={toolbarId} />
          <div onClick={propagateClick}>
            <ResizablePane
              isVertical
              minSize={MIN_EDITOR_HEIGHT}
              maxSize={1500}
              size={resizableHeight}
              onResize={(newSize) => {
                setResizableHeight(newSize);
              }}
              onResizeStart={() => setShouldAutoResize(false)}
              contentClassName={twMerge(
                "cursor-text styled-scrollbar",
                currentEditorHeight > resizableHeight
                  ? "overflow-auto"
                  : "overflow-none",
              )}
            >
              <div
                id={TIPTAP_EDITOR_SCROLLABLE_DIV_ID}
                className="h-max w-full pb-2 text-xs"
              >
                <Tiptap editor={tipTapEditor} />
              </div>
            </ResizablePane>
          </div>
          <div className="absolute bottom-1 right-1">
            <DynamicDataSelector
              side="left"
              sideOffset={200}
              onChange={handleSetDynamicData}
              targetType="text"
              initialPath={getPathFromVariable(text ?? "")}
              trigger={<DynamicDataButton />}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const TagControl: React.FC = () => {
  const { setTag, currentTag } = useRichTextComponent();
  const applyComponentAction = useApplyComponentAction();
  const store = useEditorStore();
  const text = useEditorSelector(selectDraftComponentText);
  const tagWithMixedValues = useEditorSelector(
    selectDraftComponentTextTagWithMixedValues,
  );
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();

  const onChangeTag = (tag: RichTextEditorTag) => {
    if (text && isDynamicDataValue(text)) {
      const dynamicString = naiveRemoveHtmlTags(text);
      applyComponentAction({
        type: "setProps",
        value: {
          text:
            tag === "P"
              ? `<p>${dynamicString}</p>`
              : `<h${tag}>${dynamicString}</h${tag}>`,
        },
      });
    } else {
      const actions = [];
      const savedStyle = selectSavedStyle(store.getState());
      if (isMixedStyleValue(savedStyle)) {
        const actionsWithoutSavedStyles =
          getRemoveDesignLibraryValuesActions(tag);
        if (actionsWithoutSavedStyles) {
          actions.push(...actionsWithoutSavedStyles);
        }
      }
      if (actions.length > 0) {
        applyComponentAction({
          type: "applyCompositeAction",
          value: actions,
        });
      }
      setTag(tag);
    }
  };

  return (
    <TagSelector
      currentTag={currentTag ?? tagWithMixedValues}
      onChangeTag={onChangeTag}
    />
  );
};

const FontWeightControl: React.FC<React.PropsWithChildren<unknown>> = () => {
  const applyComponentAction = useApplyComponentAction();
  const fontWeightFromSelector = useEditorSelector(selectFontWeight);
  const fontFamily = useEditorSelector(selectFontFamily);
  const store = useEditorStore();
  // NOTE (Fran 2024-11-29): If the saved style is deleted we want to show the static value to allow the
  // user change it.
  const savedStyleFontWeightValue =
    useGetDeletedSavedStyleValueIfNeeded<SavedStyleTextAttributes>(
      String(fontWeightFromSelector) ?? null,
    )?.fontWeight;
  const fontWeight = savedStyleFontWeightValue ?? fontWeightFromSelector;
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();

  const onSelect = (newValue: string) => {
    const actions = [];
    const savedStyle = selectSavedStyle(store.getState());
    if (isMixedStyleValue(savedStyle)) {
      const actionsWithoutSavedStyles = getRemoveDesignLibraryValuesActions();
      if (actionsWithoutSavedStyles) {
        actions.push(...actionsWithoutSavedStyles);
      }
    }

    applyComponentAction({
      type: "applyCompositeAction",
      value: [
        ...actions,
        {
          type: "setStyles",
          value: {
            fontWeight: newValue,
          },
        },
      ],
    });
  };

  return (
    <FontWeightSelector
      fontFamily={
        fontFamily && !isMixedStyleValue(fontFamily)
          ? fontFamily.toString()
          : null
      }
      fontWeight={!isMixedStyleValue(fontWeight) ? (fontWeight as any) : null}
      onSelect={onSelect}
    />
  );
};

const FontSizeControl: React.FC = () => {
  const fontSizeFromSelector = useEditorSelector(selectFontSize);
  // NOTE (Fran 2024-11-29): If the saved style is deleted we want to show the static value to allow the
  // user change it.
  const deletedSavedStyleValue =
    useGetDeletedSavedStyleValueIfNeeded<SavedStyleTextAttributes>(
      String(fontSizeFromSelector) ?? null,
    )?.fontSize;
  const fontSize = deletedSavedStyleValue ?? fontSizeFromSelector;

  const store = useEditorStore();
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();

  const draftComponent = useEditorSelector(selectDraftComponent);
  const lineHeight = useEditorSelector(selectLineHeight);
  const applyComponentAction = useApplyComponentAction();
  const {
    setPreviewCSSPropertyValue,
    enableCanvasPreviewCSSProperties,
    disableCanvasPreviewCSSProperties,
  } = useInCanvasPreview();

  const activeCanvas = useEditorSelector(selectActiveCanvas);

  if (!draftComponent) {
    return null;
  }

  // Note (Noah, 2022-12-09, REPL-5261): Calculate the computed value of the
  // node's font size in order to use it as a placeholder if no value is set. We
  // actually use the parent node's computed value here, to avoid an issue where
  // when you reset the font size, the input will still show the old value as
  // the placeholder because React hasn't yet completed its DOM commits (this is
  // fine, since it's the same value on the parent due to CSS cascading)
  const draftComponentNode = getEditorComponentNode({
    canvas: activeCanvas,
    componentId: draftComponent.id,
  })?.parentElement;
  const propertyForPlaceholder = draftComponentNode
    ? getComputedStyle(draftComponentNode).fontSize
    : null;

  function onPreviewChange(value: string) {
    if (!draftComponent) {
      return null;
    }

    const getAttribute = selectGetAttribute(store.getState());
    const lineHeight = getUpdatedLineHeightForFontSize(
      value,
      draftComponent,
      getAttribute,
    );
    setPreviewCSSPropertyValue(["lineHeight"], lineHeight);
  }

  function onChange(value: string) {
    if (!draftComponent) {
      return null;
    }

    const getAttribute = selectGetAttribute(store.getState());
    const lineHeight = getUpdatedLineHeightForFontSize(
      value,
      draftComponent,
      getAttribute,
    );
    const actions = [];
    const savedStyle = selectSavedStyle(store.getState());
    if (isMixedStyleValue(savedStyle)) {
      const actionsWithoutSavedStyles = getRemoveDesignLibraryValuesActions();
      if (actionsWithoutSavedStyles) {
        actions.push(...actionsWithoutSavedStyles);
      }
    }

    applyComponentAction({
      type: "applyCompositeAction",
      value: [
        ...actions,
        {
          type: "setStyles",
          value: {
            fontSize: value,
            lineHeight,
          },
        },
      ],
    });
  }

  return (
    <FontSizeSelector
      size={
        isMixedStyleValue(fontSize) ? fontSize : fontSize?.toString() ?? null
      }
      onChange={onChange}
      onPreviewChange={onPreviewChange}
      onDragStart={() => {
        const lineHeightValue = lineHeight ?? "normal";
        enableCanvasPreviewCSSProperties(
          ["lineHeight"],
          String(lineHeightValue),
        );
        setPreviewCSSPropertyValue(["lineHeight"], String(lineHeightValue));
      }}
      onDragEnd={() => {
        disableCanvasPreviewCSSProperties(["lineHeight"]);
      }}
      placeholder={propertyForPlaceholder ?? undefined}
    />
  );
};

const LineHeightControl: React.FC<React.PropsWithChildren<unknown>> = () => {
  const applyComponentAction = useApplyComponentAction();
  const store = useEditorStore();
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();

  const lineHeightDefaultValue =
    styleAttributeToEditorData.lineHeight.defaultValue;

  const lineHeightFromSelector = useEditorSelector(selectLineHeight);
  // NOTE (Fran 2024-11-29): If the saved style is deleted we want to show the static value to allow the
  // user change it.
  const deletedSavedStyleValue =
    useGetDeletedSavedStyleValueIfNeeded<SavedStyleTextAttributes>(
      String(lineHeightFromSelector) ?? null,
    )?.lineHeight;
  const lineHeight = deletedSavedStyleValue ?? lineHeightFromSelector;

  return (
    <LineHeightSelector
      lineHeight={lineHeight?.toString() ?? null}
      onChange={(newValue: string) => {
        const actions = [];
        const savedStyle = selectSavedStyle(store.getState());
        if (isMixedStyleValue(savedStyle)) {
          const actionsWithoutSavedStyles =
            getRemoveDesignLibraryValuesActions();
          if (actionsWithoutSavedStyles) {
            actions.push(...actionsWithoutSavedStyles);
          }
        }

        applyComponentAction({
          type: "applyCompositeAction",
          value: [
            ...actions,
            {
              type: "setStyles",
              value: {
                lineHeight:
                  newValue === "auto" ? lineHeightDefaultValue : newValue,
              },
            },
          ],
        });
      }}
    />
  );
};

const LetterSpacingControl: React.FC<React.PropsWithChildren<unknown>> = () => {
  const applyComponentAction = useApplyComponentAction();
  const store = useEditorStore();
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();

  return (
    <LetterSpacingSelector
      onChange={(newValue: string) => {
        const actions = [];
        const savedStyle = selectSavedStyle(store.getState());
        if (isMixedStyleValue(savedStyle)) {
          const actionsWithoutSavedStyles =
            getRemoveDesignLibraryValuesActions();
          if (actionsWithoutSavedStyles) {
            actions.push(...actionsWithoutSavedStyles);
          }
        }

        applyComponentAction({
          type: "applyCompositeAction",
          value: [
            ...actions,
            {
              type: "setStyles",
              value: {
                letterSpacing: newValue,
              },
            },
          ],
        });
      }}
    />
  );
};

const normalizeTextDirectionForInput = (
  textDirection?: CSSStyleDeclaration["textAlign"] | null,
) => {
  if (textDirection === "start") {
    return "left";
  } else if (textDirection === "end") {
    return "right";
  }
  return textDirection;
};

const TextAlignmentControl: React.FC<React.PropsWithChildren<unknown>> = () => {
  const applyComponentAction = useApplyComponentAction();
  const store = useEditorStore();
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();
  const textAlignFromSelector = useEditorSelector(selectTextAlign);
  // NOTE (Fran 2024-11-29): If the saved style is deleted we want to show the static value to allow the
  // user change it.
  const deletedSavedStyleValue =
    useGetDeletedSavedStyleValueIfNeeded<SavedStyleTextAttributes>(
      String(textAlignFromSelector) ?? null,
      true,
    )?.textAlign;
  const textAlign = deletedSavedStyleValue ?? textAlignFromSelector;
  const activeCanvas = useEditorSelector(selectActiveCanvas);
  const computedStyleValue =
    normalizeTextDirectionForInput(
      useEditorSelector((state) => {
        return selectDraftComponentComputedStyleValue(
          state,
          "textAlign",
          activeCanvas,
        );
      }),
    ) ?? null;

  const options: DeprecatedToggleOption[] = [
    {
      value: "left",
      label: <AlignLeft size={16} />,
      tooltipContent: "Align Text Left",
    },
    {
      value: "center",
      label: <AlignCenter size={16} />,
      tooltipContent: "Align Text Center",
    },
    {
      value: "right",
      label: <AlignRight size={16} />,
      tooltipContent: "Align Text Right",
    },
  ];

  return (
    <ControlGroup label="Alignment">
      <DeprecatedToggleGroup
        size="sm"
        selectedValue={
          !isMixedStyleValue(textAlign) ? textAlign ?? computedStyleValue : null
        }
        options={options}
        onChange={(newValue) => {
          const savedStyle = selectSavedStyle(store.getState());
          const actions = [];
          if (isMixedStyleValue(savedStyle)) {
            const actionsWithoutSavedStyles =
              getRemoveDesignLibraryValuesActions();
            if (actionsWithoutSavedStyles) {
              actions.push(...actionsWithoutSavedStyles);
            }
          }

          applyComponentAction({
            type: "applyCompositeAction",
            value: [
              ...actions,
              {
                type: "setStyles",
                value: { textAlign: newValue },
              },
            ],
          });
        }}
      />
    </ControlGroup>
  );
};

const TextFormatControl: React.FC = () => {
  const textDecorationFromSelector = useEditorSelector(selectTextDecoration);
  const fontStyle = useEditorSelector(selectFontStyle);
  // NOTE (Fran 2024-11-29): If the saved style is deleted we want to show the static value to allow the
  // user change it.
  const deletedSavedStyleValue =
    useGetDeletedSavedStyleValueIfNeeded<SavedStyleTextAttributes>(
      String(textDecorationFromSelector) ?? null,
    )?.textDecoration;
  const textDecoration = deletedSavedStyleValue ?? textDecorationFromSelector;
  const store = useEditorStore();
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();
  const { tipTapEditor, queueAction, clearQueuedActions } =
    useRichTextComponent();

  const applyComponentAction = useApplyComponentAction();

  const selectedValues = filterNulls(
    [textDecoration, fontStyle].map((value) => {
      return !isMixedStyleValue(value) ? String(value) : null;
    }),
  );

  function onChange(newValue: string, isSelected: boolean) {
    let chain = tipTapEditor?.chain().selectAll();
    let action: UseApplyComponentActionType | undefined;
    const actions = [];

    const savedStyle = selectSavedStyle(store.getState());
    if (isMixedStyleValue(savedStyle)) {
      const actionsWithoutSavedStyles = getRemoveDesignLibraryValuesActions();
      if (actionsWithoutSavedStyles) {
        actions.push(...actionsWithoutSavedStyles);
      }
    }

    if (newValue !== "italic") {
      let textDecoration;
      if (
        newValue === "line-through" &&
        textDecoration !== newValue &&
        isSelected
      ) {
        textDecoration = "line-through";
      } else if (
        newValue === "underline" &&
        textDecoration !== newValue &&
        isSelected
      ) {
        textDecoration = "underline";
      } else {
        textDecoration = "none";
      }

      action = {
        type: "applyCompositeAction",
        value: [
          ...actions,
          {
            type: "setStyles",
            value: {
              textDecoration,
            },
          },
        ],
      };

      queueAction(action);
      chain = chain?.unsetStrike().unsetUnderline();
    } else if (newValue === "italic") {
      let fontStyle;
      if (fontStyle !== newValue && isSelected) {
        fontStyle = "italic";
      } else {
        fontStyle = "normal";
      }

      action = {
        type: "applyCompositeAction",
        value: [
          ...actions,
          {
            type: "setStyles",
            value: {
              fontStyle,
            },
          },
        ],
      };

      queueAction(action);
      chain = chain?.unsetItalic();
    }
    const prevHtml = tipTapEditor?.getHTML();
    chain?.run();
    const postHtml = tipTapEditor?.getHTML();
    // if the html is the same, then we need to manually apply the changes
    if (prevHtml === postHtml && action) {
      applyComponentAction(action);
      clearQueuedActions();
    }
  }

  const options = [
    {
      value: "italic",
      label: (
        <div className="flex flex-1 items-center justify-center">
          <Italic size={16} />
        </div>
      ),
      tooltipContent: "Italic",
    },
    {
      value: "line-through",
      label: (
        <div className="flex flex-1 items-center justify-center">
          <Strikethrough size={16} />
        </div>
      ),
      tooltipContent: "Strikethrough",
    },
    {
      value: "underline",
      label: (
        <div className="flex flex-1 items-center justify-center">
          <Underline size={16} />
        </div>
      ),
      tooltipContent: "Underline",
    },
  ];

  const handleOnChange = (values: string[]) => {
    if (selectedValues.length > values.length) {
      const [deselectValue] = differenceBy(selectedValues, values);
      // NOTE (Chance 2023-12-05): Non-null assertion is safe here because
      // we know checked that `selectedValues.length > values.length`, which
      // means it's not empty.
      onChange(deselectValue!, false);
    } else if (values.length > selectedValues.length) {
      // NOTE (Chance 2023-12-05): Non-null assertion is safe here because
      // we know checked that `values.length > selectedValues.length`, which
      // means it's not empty.
      const [selectValue] = differenceBy(values, selectedValues);
      onChange(selectValue!, true);
    }
  };

  return (
    <ControlGroup label="Format">
      <SelectableButtons
        multiSelect
        size="sm"
        options={options}
        value={selectedValues}
        onChange={handleOnChange}
        layoutClassName="w-full"
        buttonLayoutClassName="grow"
      />
    </ControlGroup>
  );
};

const TextTransformControl: React.FC<React.PropsWithChildren> = () => {
  const applyComponentAction = useApplyComponentAction();
  const store = useEditorStore();
  const textTransform = useEditorSelector(selectTextTransform);
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();

  const handleOnChange = (newValue: string | null) => {
    const actions = [];
    const savedStyle = selectSavedStyle(store.getState());
    if (isMixedStyleValue(savedStyle)) {
      const actionsWithoutSavedStyles = getRemoveDesignLibraryValuesActions();
      if (actionsWithoutSavedStyles) {
        actions.push(...actionsWithoutSavedStyles);
      }
    }

    applyComponentAction({
      type: "applyCompositeAction",
      value: [
        ...actions,
        {
          type: "setStyles",
          value: { textTransform: !newValue ? "none" : newValue },
        },
      ],
    });
  };

  return (
    <ControlGroup label="Casing">
      <DeprecatedToggleGroup
        size="sm"
        deselectable
        selectedValue={
          !isMixedStyleValue(textTransform) ? textTransform ?? "none" : null
        }
        options={TEXT_TRANSFORM_OPTIONS}
        onChange={(value) => {
          return handleOnChange(
            value as "none" | "uppercase" | "lowercase" | "capitalize",
          );
        }}
      />
    </ControlGroup>
  );
};

const ForegroundColorControl: React.FC = () => {
  const store = useEditorStore();
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();
  // NOTE (Fran 2024-04-15): Given the text color inheritance, we need to show if the component
  // has a color or if any ancestor text color will apply.
  const ancestorTextColor = useEditorSelector(selectAncestorTextColor);
  const color = useEditorSelector(selectColor);
  const colorGradientTilt = useEditorSelector(selectColorGradientTilt);
  const colorGradientStops = useEditorSelector(
    selectColorGradientStops,
  ) as GradientStop[];

  const applyComponentAction = useApplyComponentAction();

  // if there is a instance of the tipTapEditor being used by the rich text
  // control we must use the same one and queue our changes
  const { tipTapEditor, queueAction } = useRichTextComponent();

  const changeColor = (newValue: string | SolidOrGradient) => {
    let gradientOrSolid = null;
    if (typeof newValue === "string") {
      gradientOrSolid = newValue;
    } else if (newValue.type === "solid") {
      gradientOrSolid = newValue.color;
    } else {
      gradientOrSolid = newValue.gradient;
    }

    const newStyleValue =
      gradientOrSolid && typeof gradientOrSolid === "object"
        ? {
            color: "alchemy:gradient",
            __alchemyGradient__color__tilt: gradientOrSolid?.tilt,
            __alchemyGradient__color__stops: gradientOrSolid?.stops,
            // NOTE (Fran 2024-11-27): Remove gradient data from design library when setting gradient color
            __reploGradient__color__design_library: null,
          }
        : {
            color: gradientOrSolid,
            // NOTE (Fran 2024-11-27): Remove gradient data when setting solid color
            __alchemyGradient__color__tilt: null,
            __alchemyGradient__color__stops: null,
            __reploGradient__color__design_library: null,
          };

    const actions = [];
    const savedStyle = selectSavedStyle(store.getState());
    if (isMixedStyleValue(savedStyle)) {
      const actionsWithoutSavedStyles = getRemoveDesignLibraryValuesActions();
      if (actionsWithoutSavedStyles) {
        actions.push(...actionsWithoutSavedStyles);
      }
    }

    const action: UseApplyComponentActionType = {
      type: "applyCompositeAction",
      value: [
        ...actions,
        {
          type: "setStyles",
          value: newStyleValue,
        },
      ],
    };

    // If all text in the editor already has a color then we've got to unset it
    // in order for the changes to have an effect.
    if (tipTapEditor && isAllTextColored(tipTapEditor)) {
      queueAction(action);
      tipTapEditor?.chain().selectAll().unsetColor().run();
    } else {
      applyComponentAction(action);
    }
  };

  const modifierValue = color ?? ancestorTextColor;
  // NOTE (Fran 2024-11-29): If the saved style is deleted we want to show the static value to allow the
  // user change it.
  const deletedSavedStyleValue =
    useGetDeletedSavedStyleValueIfNeeded<SavedStyleTextAttributes>(
      String(modifierValue) ?? null,
    )?.color;
  const colorValue = deletedSavedStyleValue ?? modifierValue;

  const onRemove = () => {
    // NOTE (Fran 2024-05-09): If the color is not set we will show the inherited color from any
    // ancestor, so in this case, we should set the color to transparent. If the color is set, we
    // should remove it and show the inherited color.
    const onRemoveNewColor = color ? null : "#00000000";
    changeColor({
      type: "solid",
      color: onRemoveNewColor,
    });
  };

  const isMixedGradient =
    isMixedStyleValue(colorGradientTilt) ||
    isMixedStyleValue(colorGradientStops);

  return (
    <ControlGroup label="Color">
      <DynamicColorModifier
        previewProperty="color"
        popoverTitle="Text Color"
        gradientSelectionType="color"
        gradientData={
          isMixedGradient
            ? REPLO_MIXED_STYLE_VALUE
            : {
                tilt: coerceNumberToString(colorGradientTilt) ?? "90deg",
                stops: colorGradientStops ?? [
                  {
                    id: "c7795a8c-4e13-4011-889b-64adb0e11e41",
                    color: "#df9393",
                    location: "0%",
                  },
                ],
              }
        }
        field="style.color"
        value={colorValue ?? undefined}
        onChange={changeColor}
        onRemove={onRemove}
        popoverSideOffset={BADGE_TRIGGER_OFFSET}
        showSavedStyles
      />
    </ControlGroup>
  );
};

const TextOutlineControl: React.FC = () => {
  const logEvent = useLogAnalytics();
  const applyComponentAction = useApplyComponentAction();
  const store = useEditorStore();
  const componentId = useEditorSelector(selectDraftComponentId);
  const textOutlineFromSelector = useEditorSelector(selectTextOutline);
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();

  const widthValue = textOutlineFromSelector[0];
  const colorValue = textOutlineFromSelector[1];

  // NOTE (Fran 2024-11-29): If the saved style is deleted we want to show the static value to allow the
  // user change it.
  const deletedSavedStyleValue =
    useGetDeletedSavedStyleValueIfNeeded<SavedStyleTextAttributes>(
      !isMixedStyleValue(colorValue) ? colorValue ?? null : null,
    )?.color;

  if (!componentId) {
    return null;
  }

  const textOutlineValue = deletedSavedStyleValue
    ? {
        width: widthValue ?? "1px",
        color: deletedSavedStyleValue,
      }
    : {
        width: widthValue ?? "1px",
        color: colorValue ?? "#000000",
      };

  const handleInputChange = (value: string, inputType: "width" | "color") => {
    let textOutlineString = null;
    if (value && value !== "0px") {
      const newTextOutline = {
        width:
          (!isMixedStyleValue(textOutlineValue?.width) &&
            textOutlineValue?.width) ||
          "1px",
        color: (() => {
          if (isMixedStyleValue(textOutlineValue?.color)) {
            return "#000000";
          }
          if (isDynamicDesignLibraryValue(textOutlineValue?.color)) {
            return textOutlineValue?.color;
          }
          return getHexColor(textOutlineValue?.color) || "#000000";
        })(),
        [inputType]: value,
      };
      textOutlineString = getTextOutlineString(newTextOutline);
    }

    const actions = [];
    const savedStyle = selectSavedStyle(store.getState());
    if (isMixedStyleValue(savedStyle)) {
      const actionsWithoutSavedStyles = getRemoveDesignLibraryValuesActions();
      if (actionsWithoutSavedStyles) {
        actions.push(...actionsWithoutSavedStyles);
      }
    }

    applyComponentAction({
      type: "applyCompositeAction",
      value: [
        ...actions,
        {
          type: "setStyles",
          value: {
            __textStroke: textOutlineString,
          },
        },
      ],
    });
  };

  const onSavedOutlineSelect = (value: string) => {
    let textOutlineString = null;

    if (value) {
      const newTextOutline = {
        width:
          (!isMixedStyleValue(textOutlineValue?.width) &&
            textOutlineValue?.width) ||
          "1px",
        color: value,
      };
      textOutlineString = getTextOutlineString(newTextOutline);
    }
    applyComponentAction({
      type: "setStyles",
      value: {
        __textStroke: textOutlineString,
      },
    });
    logEvent("library.style.apply", {
      modifier: "textOutlineColor",
      type: "color",
    });
  };

  return (
    <>
      <LengthInputSelector.Root
        metrics={CSS_LENGTH_TYPES}
        className="col-span-1"
        minDragValues={{ px: 0 }}
        minValues={{ px: 0 }}
        maxValues={{ px: 30 }}
        maxDragValues={{ px: 30 }}
        field="width"
        resetValue="0px"
        anchorValue="0px"
        value={textOutlineValue?.width ?? null}
        onChange={(value: string) => handleInputChange(value, "width")}
        previewProperty="__textStroke"
        previewSubProperty="width"
        dragTrigger="label"
      >
        {/* NOTE (Fran 2024-10-16): 74px is the fixed width of the label. */}
        <div className="grid w-full grid-cols-[74px,auto] items-center gap-y-2">
          <LengthInputSelector.DraggableArea>
            <ModifierLabel label="Outline" />
          </LengthInputSelector.DraggableArea>
          <LengthInputSelector.Input
            placeholder="0px"
            startEnhancer={<Baseline size={12} />}
          />
          <div className="col-start-2">
            <DynamicColorSelector
              field="color"
              componentId={componentId}
              allowsGradientSelection={false}
              popoverTitle="Color"
              value={textOutlineValue?.color ?? null}
              onChange={(value: string) => handleInputChange(value, "color")}
              popoverSideOffset={BADGE_TRIGGER_OFFSET}
              showSavedStyles
              onSelectSavedStyle={onSavedOutlineSelect}
            />
          </div>
        </div>
      </LengthInputSelector.Root>
    </>
  );
};

const TextShadowControl: React.FC = () => {
  const textShadow = useEditorSelector(selectTextShadow);
  const textShadows = useEditorSelector(selectTextShadows);
  const savedStyle = useEditorSelector(selectSavedStyle);
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();

  // NOTE (Fran 2024-11-29): If the saved style is deleted we want to show the static value to allow the
  // user change it.
  const deletedSavedStyleValue =
    useGetDeletedSavedStyleValueIfNeeded<SavedStyleTextAttributes>(
      String(textShadow) ?? null,
    )?.textShadow;
  const savedStyleParsedTextShadows = deletedSavedStyleValue
    ? parseTextShadows(deletedSavedStyleValue.split(",") ?? "")
    : // TODO (Fran 2024-11-29 REPL-14885): Allow add a text shadow if there is no shadow selected in the
      // deleted saved style
      [DEFAULT_TEXT_SHADOW];

  const resolvedTextShadows = isDynamicDesignLibraryValue(String(textShadow))
    ? savedStyleParsedTextShadows
    : textShadows;

  const openPopoverId = useEditorSelector(selectOpenPopoverId);
  const dispatch = useEditorDispatch();

  const applyComponentAction = useApplyComponentAction();

  const { colorShopStyles: colorSavedStyles } = useShopStyles();

  if (!resolvedTextShadows) {
    return null;
  }

  const isPopoverOpen =
    openPopoverId &&
    typeof openPopoverId === "object" &&
    "text-shadow" in openPopoverId;

  const currentShadowIndex = isPopoverOpen
    ? openPopoverId["text-shadow"]
    : null;
  const currentShadow =
    currentShadowIndex != null ? resolvedTextShadows[currentShadowIndex] : null;

  const createOrUpdateTextShadow = (value: string) => {
    const actions = [];
    if (isMixedStyleValue(savedStyle)) {
      const actionsWithoutSavedStyles = getRemoveDesignLibraryValuesActions();
      if (actionsWithoutSavedStyles) {
        actions.push(...actionsWithoutSavedStyles);
      }
    }

    applyComponentAction({
      type: "applyCompositeAction",
      value: [
        ...actions,
        {
          type: "setStyles",
          value: {
            textShadow: value,
          },
        },
      ],
    });
  };

  const handleRemoveTextShadow = (index: number) => {
    // TODO (Martin 2025-01-22): Figure out how to remove a single value since
    // we store shadows as a single string.
    if (
      resolvedTextShadows.some((textShadow) => isMixedStyleValue(textShadow))
    ) {
      createOrUpdateTextShadow("none");
      return;
    }

    const filteredTextShadows = resolvedTextShadows.filter(
      (_, shadowIndex) => shadowIndex !== index,
    );
    const updatedTextShadows = getTextShadowString(
      filteredTextShadows as TextShadow[],
    );
    createOrUpdateTextShadow(updatedTextShadows);
  };

  const handleTextShadowChange = (value: TextShadow, index: number) => {
    // TODO (Martin 2025-01-22): Figure out how to update a single value since
    // we store shadows as a single string.
    if (
      resolvedTextShadows.some((textShadow) => isMixedStyleValue(textShadow))
    ) {
      createOrUpdateTextShadow(getTextShadowString([value]));
      return;
    }

    const newTextShadows = resolvedTextShadows.map((textShadow, i) => {
      return i === index ? value : textShadow;
    });
    const updatedTextShadows = getTextShadowString(
      newTextShadows as TextShadow[],
    );
    createOrUpdateTextShadow(updatedTextShadows);
  };

  const getBadgeColor = (color?: string) => {
    if (color && isDynamicDesignLibraryValue(color)) {
      const savedStyle = getSavedStyleValue(colorSavedStyles, color);
      return savedStyle?.attributes && "color" in savedStyle.attributes
        ? String(savedStyle.attributes.color)
        : "text-muted";
    }
    return color ?? "text-muted";
  };

  return (
    // NOTE (Fran 2024-10-16): 74px is the fixed width of the label.
    <div className="grid w-full grid-cols-[74px,auto] items-center">
      <ModifierLabel label="Shadow" />
      {resolvedTextShadows.map((textShadow, index) => {
        const isMixedTextShadow = isMixedStyleValue(textShadow);
        return (
          <div className="col-start-2 py-1" key={`textShadow-${index}`}>
            <SelectionIndicator
              className="max-w-40"
              title={
                !isMixedTextShadow
                  ? formatTitle(textShadow, colorSavedStyles)
                  : "Mixed"
              }
              onClick={() => {
                return dispatch(setOpenPopoverId({ "text-shadow": index }));
              }}
              startEnhancer={
                <Badge
                  type={!isMixedTextShadow ? "color" : "unknown"}
                  isFilled
                  backgroundColor={
                    !isMixedTextShadow ? getBadgeColor(textShadow.color) : ""
                  }
                />
              }
              endEnhancer={
                <FormFieldXButton
                  onClick={(event) => {
                    // NOTE (Sebas, 2024-10-10): This is necessary to prevent executing the onClick event to avoid open
                    // the popover when the user wants to remove the shadow.
                    event.stopPropagation();
                    handleRemoveTextShadow(index);
                  }}
                />
              }
            />
          </div>
        );
      })}
      {currentShadow ? (
        <TextShadowPopover
          isOpen={isPopoverOpen ?? false}
          activeTextShadow={currentShadow}
          handleTextShadowChange={(value) => {
            return handleTextShadowChange(value, currentShadowIndex ?? 0);
          }}
          textShadowIndex={currentShadowIndex ?? 0}
          onRequestClose={() => dispatch(setOpenPopoverId(null))}
        />
      ) : null}
    </div>
  );
};

const TextShadowPopover: React.FC<
  React.PropsWithChildren<{
    isOpen: boolean;
    activeTextShadow: TextShadow | ReploMixedStyleValue;
    textShadowIndex: number;
    handleTextShadowChange(value: TextShadow, index: number): void;
    onRequestClose(): void;
  }>
> = ({
  isOpen,
  activeTextShadow,
  handleTextShadowChange,
  textShadowIndex,
  onRequestClose,
}) => {
  const areModalsOpen = useAreModalsOpen();
  const isMixedTextShadow = isMixedStyleValue(activeTextShadow);

  const handleInputChange = (
    value: string,
    inputType: "offsetX" | "offsetY" | "blur" | "color",
  ) => {
    const newTextShadow = {
      ...(!isMixedTextShadow ? activeTextShadow : DEFAULT_TEXT_SHADOW),
      [inputType]: value,
    };
    handleTextShadowChange(newTextShadow, textShadowIndex);
  };

  return (
    <Popover.Root
      isOpen={isOpen}
      onOpenChange={(newValue: boolean) => {
        if (!newValue) {
          onRequestClose();
        }
      }}
    >
      <Popover.Content
        title="Text Shadow"
        shouldPreventDefaultOnInteractOutside={areModalsOpen}
        onRequestClose={onRequestClose}
      >
        <TextShadowPopoverContent
          offsetX={
            !isMixedTextShadow ? activeTextShadow.offsetX : activeTextShadow
          }
          offsetY={
            !isMixedTextShadow ? activeTextShadow.offsetY : activeTextShadow
          }
          blur={!isMixedTextShadow ? activeTextShadow.blur : activeTextShadow}
          color={!isMixedTextShadow ? activeTextShadow.color : activeTextShadow}
          textShadowIndex={textShadowIndex}
          handleInputChange={handleInputChange}
        />
      </Popover.Content>
      <Popover.Anchor className="relative left-0 top-0" />
    </Popover.Root>
  );
};

const TextShadowPopoverContent: React.FC<{
  offsetX?: string | ReploMixedStyleValue;
  offsetY?: string | ReploMixedStyleValue;
  blur?: string | ReploMixedStyleValue;
  color?: string | ReploMixedStyleValue;
  textShadowIndex: number;
  handleInputChange(
    value: string,
    inputType: "offsetX" | "offsetY" | "blur" | "color",
  ): void;
}> = ({
  offsetX,
  offsetY,
  blur,
  color,
  textShadowIndex,
  handleInputChange,
}) => {
  const draftComponentId = useEditorSelector(selectDraftComponentId);
  const logEvent = useLogAnalytics();

  return (
    <div className="flex flex-col gap-2">
      <LengthInputSelector
        label={<ModifierLabel label="X Axis" />}
        metrics={CSS_LENGTH_TYPES}
        className="col-span-1"
        field="offsetX"
        resetValue="0px"
        anchorValue="0px"
        placeholder="0px"
        value={offsetX || null}
        onChange={(value: string) => handleInputChange(value, "offsetX")}
        previewProperty="textShadow"
        previewSubProperty="offsetX"
        previewPropertyIndex={textShadowIndex}
        draggingDirection="negative"
        autofocus
        dragTrigger="label"
      />
      <LengthInputSelector
        metrics={CSS_LENGTH_TYPES}
        className="col-span-1"
        label={<ModifierLabel label="Y Axis" />}
        field="offsetY"
        resetValue="0px"
        anchorValue="0px"
        placeholder="0px"
        value={offsetY || null}
        onChange={(value: string) => handleInputChange(value, "offsetY")}
        previewProperty="textShadow"
        previewSubProperty="offsetY"
        previewPropertyIndex={textShadowIndex}
        dragTrigger="label"
        draggingDirection="negative"
      />
      <LengthInputSelector
        label={<ModifierLabel label="Blur" />}
        metrics={CSS_LENGTH_TYPES}
        className="col-span-1"
        minDragValues={{ px: 0 }}
        minValues={{ px: 0 }}
        field="blur"
        resetValue="0px"
        anchorValue="0px"
        placeholder="0px"
        value={blur || null}
        onChange={(value: string) => handleInputChange(value, "blur")}
        previewProperty="textShadow"
        previewSubProperty="blur"
        previewPropertyIndex={textShadowIndex}
        dragTrigger="label"
      />
      <div className="flex items-center">
        <ModifierLabel label="Color" />
        <DynamicColorSelector
          field="color"
          componentId={draftComponentId ?? ""}
          allowsGradientSelection={false}
          popoverTitle="Color"
          value={color ?? null}
          onChange={(value: string) => handleInputChange(value, "color")}
          showSavedStyles
          onSelectSavedStyle={(value: string) => {
            logEvent("library.style.apply", {
              modifier: "textShadowColor",
              type: "color",
            });
            handleInputChange(value, "color");
          }}
          onRemove={() => handleInputChange("", "color")}
        />
      </div>
    </div>
  );
};

const TextSavedStyleGroup: React.FC = () => {
  const applyComponentAction = useApplyComponentAction();
  const savedStyle = useEditorSelector(selectSavedStyle);
  const store = useEditorStore();
  const logEvent = useLogAnalytics();
  const { getRemoveDesignLibraryValuesActions } =
    useResetDesignLibraryTextValue();
  const onSelectSavedStyle = (value: string) => {
    const textsAndIds = selectDraftComponentsInnerTextsAndIds(store.getState());

    logEvent("library.style.apply", {
      type: "text",
      modifier: "text",
    });

    const setPropActions =
      textsAndIds?.map(({ id, texts }) => ({
        type: "setProps" as const,
        componentId: id,
        value: {
          text: texts
            ?.map((text) => {
              return `<{{ ${value}.htmlTag }}>${text}</{{ ${value}.htmlTag }}>`;
            })
            .join(""),
        },
      })) ?? [];

    applyComponentAction({
      type: "applyCompositeAction",
      value: [
        ...setPropActions,
        {
          type: "setStyles",
          value: {
            fontFamily: `{{ ${value}.fontFamily }}`,
            fontSize: `{{ ${value}.fontSize }}`,
            fontWeight: `{{ ${value}.fontWeight }}`,
            letterSpacing: `{{ ${value}.letterSpacing }}`,
            lineHeight: `{{ ${value}.lineHeight }}`,
            fontStyle: `{{ ${value}.textDecoration }}`,
          },
        },
      ],
    });
  };

  const onSavedStyleRemove = () => {
    const textsAndIds = selectDraftComponentsInnerTextsAndIds(store.getState());

    if (isMixedStyleValue(savedStyle)) {
      const actionsWithoutSavedStyles = getRemoveDesignLibraryValuesActions();
      if (actionsWithoutSavedStyles) {
        applyComponentAction({
          type: "applyCompositeAction",
          value: actionsWithoutSavedStyles,
        });
      }
    } else {
      const setPropActions =
        textsAndIds?.map(({ id, texts }) => ({
          type: "setProps" as const,
          componentId: id,
          value: {
            text: texts?.map((text) => `<p>${text}</p>`).join(""),
          },
        })) ?? [];

      applyComponentAction({
        type: "applyCompositeAction",
        value: [
          ...setPropActions,
          {
            type: "setStyles",
            value: {
              color: null,
              fontFamily: null,
              fontSize: null,
              fontWeight: null,
              letterSpacing: null,
              lineHeight: null,
              textAlign: null,
              textDecoration: null,
              textTransform: null,
              textShadow: null,
              __textStroke: null,
            },
          },
        ],
      });
    }
  };

  return (
    <ControlGroup label="Style">
      <TextShopStyleModifier
        popoverSideOffset={84}
        savedStyleValueReference={
          isMixedStyleValue(savedStyle) ? savedStyle : String(savedStyle) ?? ""
        }
        onSelectSavedStyle={onSelectSavedStyle}
        onRemove={onSavedStyleRemove}
      />
    </ControlGroup>
  );
};

function useTextStyleHotkeys() {
  const globalActions = useGlobalEditorActions();
  const enableNonDynamicTextEditing = useEnableNonDynamicTextEditing();
  const areModalsOpen = useAreModalsOpen();

  useReploHotkeys({
    toggleBoldText: globalActions.toggleBoldText,
    toggleH1Text: globalActions.toggleH1Text,
    toggleH2Text: globalActions.toggleH2Text,
    toggleH3Text: globalActions.toggleH3Text,
    toggleH4Text: globalActions.toggleH4Text,
    toggleH5Text: globalActions.toggleH5Text,
    toggleH6Text: globalActions.toggleH6Text,
    toggleBulletList: globalActions.toggleBulletList,
    toggleNumberedList: globalActions.toggleNumberedList,
    toggleLinkText: globalActions.toggleLinkText,
    toggleItalicText: globalActions.toggleItalicText,
    toggleUnderlineText: globalActions.toggleUnderlineText,
    toggleStrikethroughText: globalActions.toggleStrikethroughText,
    decreaseFontSize: globalActions.decreaseFontSize,
    increaseFontSize: globalActions.increaseFontSize,
    decreaseLetterSpacing: globalActions.decreaseLetterSpacing,
    increaseLetterSpacing: globalActions.increaseLetterSpacing,
    decreaseLineHeight: globalActions.decreaseLineHeight,
    increaseLineHeight: globalActions.increaseLineHeight,
    decreaseFontWeight: globalActions.decreaseFontWeight,
    increaseFontWeight: globalActions.increaseFontWeight,
    ...(!areModalsOpen
      ? {
          editText: [
            enableNonDynamicTextEditing,
            { disallowWhenDialogsAndPopoversOpen: true },
          ],
        }
      : {}),
  });
}

const useAddDefaultShadow = () => {
  const store = useEditorStore();
  const applyComponentAction = useApplyComponentAction();
  const dispatch = useEditorDispatch();

  return () => {
    const draftComponentTextShadow = selectTextShadow(store.getState());
    const draftComponentId = selectDraftComponentId(store.getState());
    const draftComponentTextShadows = !isMixedStyleValue(
      draftComponentTextShadow,
    )
      ? draftComponentTextShadow?.split(",") ?? []
      : [];
    const textShadows =
      draftComponentTextShadows.length > 0
        ? parseTextShadows(draftComponentTextShadows)
        : [];
    const updatedTextShadows = getTextShadowString([
      ...textShadows,
      DEFAULT_TEXT_SHADOW,
    ]);

    const lastIndex = textShadows.length;
    dispatch(setOpenPopoverId({ "text-shadow": lastIndex }));

    applyComponentAction({
      componentId: draftComponentId,
      type: "setStyles",
      value: {
        textShadow: updatedTextShadows,
      },
    });
  };
};

export default TextStyleModifier;
